import React, { useEffect, useRef, memo } from "react";
import { MapService } from "../services/mapService";
import { MAP_DEFAULTS, MapMode } from "../types";
import debugx from "debugx";

const debug = debugx("bikedash:map:view");

interface MapViewProps {
  id?: string; // Allow multiple map instances
  onMapReady?: (mapService: MapService) => void;
  initialZoom?: number;
  initialMode?: MapMode;
  initialCenter?: [number, number]; // [lng, lat]
  initialStyleId?: string;
  apiKeys?: Record<string, string>;
}

// This component should NEVER re-render after mount
export const MapView = memo(
  function MapView({
    id = "main",
    onMapReady,
    initialZoom,
    initialMode,
    initialCenter,
    initialStyleId,
    apiKeys,
  }: MapViewProps) {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapServiceRef = useRef<MapService | null>(null);

    useEffect(() => {
      if (!mapContainerRef.current || mapServiceRef.current) return;

      debug(
        `Initializing map instance: ${id} with zoom=${initialZoom}, mode=${initialMode}, center=${initialCenter}, style=${initialStyleId}`,
      );

      // Create map service with saved preferences or defaults
      const mapService = new MapService({
        target: mapContainerRef.current,
        center: initialCenter ?? MAP_DEFAULTS.center,
        zoom: initialZoom ?? MAP_DEFAULTS.zoom,
        mode: initialMode ?? "follow",
        styleId: initialStyleId,
        apiKeys: apiKeys,
      });

      mapServiceRef.current = mapService;

      // Register map globally for direct access from services
      if (!window.__bikedash_maps) {
        window.__bikedash_maps = {};
      }
      window.__bikedash_maps[id] = mapService;

      // Notify parent that map is ready
      if (onMapReady) {
        onMapReady(mapService);
      }

      debug(`Map instance '${id}' ready`);

      // Cleanup
      return () => {
        debug(`Destroying map instance: ${id}`);
        mapService.destroy();
        mapServiceRef.current = null;
        delete window.__bikedash_maps?.[id];
      };
    }, []); // EMPTY DEPS - NEVER RE-RUN

    return (
      <div
        ref={mapContainerRef}
        className="map-view"
        style={{ width: "100%", height: "100%" }}
        data-map-id={id}
      />
    );
  },
  // Custom comparison function - ALWAYS return true to prevent re-renders
  (prevProps, nextProps) => {
    // Even if props change, we don't want to re-render
    // The map service handles all updates internally
    return true;
  },
);

// TypeScript declaration for global map access
declare global {
  interface Window {
    __bikedash_maps?: Record<string, MapService>;
  }
}
